/**
 * Escolher, no painel, uma folha de sprites do acervo — e ver o que se escolheu.
 *
 * O painel tinha listas de nomes escritas à mão dentro de cada editor. Elas
 * envelhecem por dois lados: um jogo importado traz folhas que nenhuma delas
 * menciona, e uma folha removida do acervo continua oferecida. Aqui a lista
 * sai do manifesto, que é a única coisa no projeto que sabe de fato o que
 * existe em disco — inclusive o que os jogos importados acrescentaram, com o
 * prefixo deles.
 *
 * O que estava gravado no registro entra na lista mesmo que o manifesto não o
 * conheça. Um asset pode ter sumido, e abrir o editor não deveria trocar
 * silenciosamente a aparência de um personagem que ninguém pediu para mudar.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ASSETS_BASE,
  loadManifest,
  walkFrames,
  type ImageEntry,
  type SpriteGrid,
} from '../assets/manifest.js';
import { mapArtPreviewScale } from '../render/map-art.js';

/**
 * O grupo viaja junto com a folha.
 *
 * Um battler de monstro mora ora em `enemies`, ora em `sv_enemies`, e um jogo
 * importado costuma trazer só uma das duas galerias. Guardar de onde cada folha
 * veio é o que permite montar o caminho certo sem adivinhar pela extensão.
 */
type Sheet = ImageEntry & { readonly group: string };
type Catalog = Readonly<Record<string, Sheet>>;

/** As folhas de um ou mais grupos de imagens do acervo, do manifesto. */
export function useImageCatalog(...groups: readonly string[]): Catalog {
  const [catalog, setCatalog] = useState<Catalog>({});
  const key = groups.join(',');

  useEffect(() => {
    let live = true;
    void loadManifest()
      .then((manifest) => {
        if (!live) return;
        const merged: Record<string, Sheet> = {};
        for (const group of key.split(',')) {
          for (const [stem, entry] of Object.entries(manifest.images[group] ?? {})) {
            merged[stem] = { ...entry, group };
          }
        }
        setCatalog(merged);
      })
      .catch(() => {
        // Manifesto fora do ar: os editores caem na lista de reserva deles, e
        // continuam podendo salvar o que já estava gravado.
      });
    return () => {
      live = false;
    };
  }, [key]);

  return catalog;
}

/**
 * Os nomes oferecidos na lista: os do acervo, mais o que já estava gravado.
 *
 * `fallback` cobre o intervalo em que o manifesto ainda não chegou — e o caso
 * em que ele não chega nunca.
 */
export function sheetOptions(
  catalog: Catalog,
  current: string,
  fallback: readonly string[] = [],
): readonly string[] {
  const known = Object.keys(catalog);
  const names = new Set(known.length > 0 ? known : fallback);
  if (current) names.add(current);
  return [...names].sort((a, b) => a.localeCompare(b));
}

/**
 * O caminho da folha, pelo manifesto quando ele a conhece.
 *
 * `group` é só o palpite de reserva: quando a folha está no catálogo, quem
 * manda é a galeria de onde ela veio.
 */
export function sheetUrl(catalog: Catalog, group: string, sheet: string): string {
  const entry = catalog[sheet];
  const file = entry?.file ?? `${sheet}.webp`;
  return `${ASSETS_BASE}/img/${entry?.group ?? group}/${encodeURIComponent(file)}`;
}

/**
 * Quantos personagens cabem na folha.
 *
 * As folhas do RPG Maker marcadas com `$` guardam um personagem só, grande; as
 * demais guardam oito, num arranjo de quatro por dois. Oferecer oito índices
 * numa folha de um só recorta o vazio, e é assim que um sprite some do mapa
 * sem explicação.
 */
export function charactersInSheet(catalog: Catalog, sheet: string): number {
  const grid = catalog[sheet]?.grid;
  if (grid) return grid.charactersPerSheet ?? 1;
  return sheet.includes('$') ? 1 : 8;
}

/** A grade de reserva, para a folha que o manifesto não conhece. */
export const DEFAULT_GRID: SpriteGrid = {
  frameWidth: 48,
  frameHeight: 48,
  columns: 12,
  rows: 8,
  charactersPerSheet: 8,
  framesPerCharacter: 12,
};

/**
 * O recorte de um quadro da folha, como fundo de uma caixa.
 *
 * Posição em porcentagem não é a fração do quadro: o navegador alinha a fração
 * da IMAGEM que sobra com a fração da caixa que sobra, e por isso o divisor é
 * `colunas - 1`. Com `colunas` no lugar, todo quadro sai deslocado — de leve no
 * primeiro, meio sprite no último.
 */
export function frameBackground(grid: SpriteGrid, frame: number): {
  backgroundSize: string;
  backgroundPosition: string;
} {
  const column = frame % grid.columns;
  const row = Math.floor(frame / grid.columns);
  const across = grid.columns > 1 ? (column / (grid.columns - 1)) * 100 : 0;
  const down = grid.rows > 1 ? (row / (grid.rows - 1)) * 100 : 0;
  return {
    backgroundSize: `${grid.columns * 100}% ${grid.rows * 100}%`,
    backgroundPosition: `${across}% ${down}%`,
  };
}

/** O sprite de mapa parado, virado para baixo — como a unidade nasce no tabuleiro. */
export function MapSpritePreview({
  catalog,
  sheet,
  index,
  className = 'admin-map-sprite-preview',
}: {
  readonly catalog: Catalog;
  readonly sheet: string;
  readonly index: number;
  readonly className?: string;
}): JSX.Element {
  const grid = catalog[sheet]?.grid ?? DEFAULT_GRID;
  // O quadro do meio da fileira é o de pé parado; os outros dois são passos.
  const frame = walkFrames(grid, index, 0)[1] ?? 0;

  return (
    <div
      className={className}
      style={{
        backgroundImage: `url(${sheetUrl(catalog, 'characters', sheet)})`,
        backgroundRepeat: 'no-repeat',
        ...frameBackground(grid, frame),
      }}
    />
  );
}

export interface MapSpriteValue {
  readonly sheet: string;
  readonly index: number;
}

/**
 * O seletor completo: prévia, folha e posição dentro dela.
 *
 * Usado pelo editor de personagens e pelo de inimigos — a aparência no
 * tabuleiro é a mesma coisa nos dois, e escrevê-la duas vezes é como as duas
 * telas começam a divergir.
 */
/** A arte que entra no tabuleiro quando a unidade não tem folha própria. */
export interface FallbackArt {
  readonly url: string;
  readonly width: number;
  readonly height: number;
}

/** O tamanho da caixa de prévia, em pixels — o mesmo do CSS. */
const PREVIEW_BOX = 96;

/**
 * O desenho de combate na proporção em que ele vai ao tabuleiro.
 *
 * O que se vê aqui é a comparação de tamanho que a partida vai aplicar: o
 * maior battler possível preenche a caixa, e um ovo com pernas ocupa a fração
 * dela que ocupará da casa.
 */
function FallbackPreview({ art }: { readonly art: FallbackArt }): JSX.Element {
  const scale = mapArtPreviewScale(art.width, art.height, PREVIEW_BOX);
  return (
    <div className="admin-map-sprite-preview is-inherited">
      <img
        src={art.url}
        alt=""
        style={{ width: art.width * scale, height: art.height * scale }}
      />
    </div>
  );
}

export function MapSpritePicker({
  value,
  onChange,
  label = 'Sprite no Mapa',
  fallbackSheets = [],
  emptyLabel,
  fallbackArt,
  fallbackNote,
}: {
  readonly value: MapSpriteValue;
  readonly onChange: (next: MapSpriteValue) => void;
  readonly label?: string;
  readonly fallbackSheets?: readonly string[];
  /** Quando existe, a lista aceita "sem folha" — e é isto que ela mostra. */
  readonly emptyLabel?: string;
  /**
   * O que aparece na prévia enquanto nenhuma folha está escolhida.
   *
   * Sem isto a caixa mostrava um "?", e "?" é a única resposta errada: a
   * unidade SEMPRE tem uma aparência no tabuleiro — se não é a folha, é o
   * desenho de combate dela. Quem edita precisa ver o que vai para o campo,
   * não um aviso de que ainda não escolheu nada.
   */
  readonly fallbackArt?: FallbackArt | null;
  readonly fallbackNote?: string;
}): JSX.Element {
  const catalog = useImageCatalog('characters');
  const options = useMemo(
    () => sheetOptions(catalog, value.sheet, fallbackSheets),
    [catalog, value.sheet, fallbackSheets],
  );
  const slots = charactersInSheet(catalog, value.sheet);

  return (
    <div className="admin-preview-card">
      <span className="admin-preview-label">{label}</span>

      {value.sheet ? (
        <MapSpritePreview catalog={catalog} sheet={value.sheet} index={value.index} />
      ) : fallbackArt ? (
        <FallbackPreview art={fallbackArt} />
      ) : (
        <div className="admin-map-sprite-preview is-empty" aria-hidden="true">
          ?
        </div>
      )}

      {!value.sheet && fallbackArt && fallbackNote && (
        <span className="admin-preview-hint">{fallbackNote}</span>
      )}

      <div className="admin-preview-selectors">
        <select
          className="admin-select-small"
          value={value.sheet}
          aria-label={`${label}: folha`}
          onChange={(e) => {
            const sheet = e.target.value;
            // Trocar para uma folha de um personagem só invalida o índice
            // antigo: sem este corte, o sprite passa a recortar o nada.
            const limit = charactersInSheet(catalog, sheet);
            onChange({ sheet, index: Math.min(value.index, Math.max(0, limit - 1)) });
          }}
        >
          {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
          {options.map((sheet) => (
            <option key={sheet} value={sheet}>
              {sheet}
            </option>
          ))}
        </select>

        {slots > 1 && (
          <select
            className="admin-select-small"
            value={value.index}
            aria-label={`${label}: posição`}
            disabled={!value.sheet}
            onChange={(e) => onChange({ sheet: value.sheet, index: Number(e.target.value) })}
          >
            {Array.from({ length: slots }, (_, i) => (
              <option key={i} value={i}>
                Posição {i}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
